import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useTranslation } from '../../context/I18nContext';

const { FiMapPin, FiStar, FiShoppingBag, FiCheck, FiClock, FiSun, FiTruck } = FiIcons;

export const ProductCard = ({ product, delay = 0 }) => {
  const { t } = useTranslation();

  const getStatusIcon = (status) => {
    if (status.includes('Vandaag')) return FiCheck;
    if (status.includes('Morgen')) return FiClock;
    return FiSun;
  };

  const getStatusColor = (status) => {
    if (status.includes('Vandaag')) return 'text-emerald-600 bg-emerald-50';
    if (status.includes('Morgen') || status.includes('week')) return 'text-blue-600 bg-blue-50';
    return 'text-amber-600 bg-amber-50';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      className="group flex flex-col"
    >
      <div className="relative aspect-[4/5] rounded-[2.5rem] overflow-hidden mb-6 bg-zinc-100 shadow-sm transition-all duration-700 group-hover:shadow-2xl group-hover:shadow-zinc-300">
        <img 
          src={product.image_url} 
          alt={product.name} 
          className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
        />
        
        {/* Overlay Badges */}
        <div className="absolute top-6 left-6 flex flex-col gap-2">
          <span className="px-4 py-1.5 bg-white/95 backdrop-blur-md rounded-full text-[9px] font-black uppercase tracking-widest shadow-sm text-zinc-900 border border-zinc-100">
            {product.category_label}
          </span>
          <span className={`px-4 py-1.5 backdrop-blur-md rounded-full text-[9px] font-black uppercase tracking-widest shadow-sm flex items-center gap-1.5 border border-white/20 ${getStatusColor(product.status_text)}`}>
            <SafeIcon icon={getStatusIcon(product.status_text)} className="w-3 h-3" />
            {product.status_text}
          </span>
        </div>

        {/* Hover Action */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500">
          <Link to={`/product/${product.id}`}>
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-zinc-900 px-8 py-4 rounded-full font-black text-[10px] uppercase tracking-[0.2em] shadow-2xl transform translate-y-4 group-hover:translate-y-0 transition-all duration-500"
            >
              {t('common.viewDetails')}
            </motion.button>
          </Link>
        </div>
      </div>
      
      <div className="px-2">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0 ring-1 ring-zinc-100 shadow-sm">
              <img src={product.supplier_image} className="w-full h-full object-cover" alt="" />
            </div>
            <span className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-400">{product.supplier_name}</span>
          </div>
          <div className="flex items-center gap-1 text-[10px] font-black text-zinc-900 bg-white px-2 py-0.5 rounded-full border border-zinc-50 shadow-sm">
            <SafeIcon icon={FiStar} className="w-3 h-3 text-amber-400 fill-amber-400" />
            {product.rating}
          </div>
        </div>

        <h3 className="text-xl font-bold text-zinc-900 mb-1.5 group-hover:text-[#1A2E22] transition-colors tracking-tight line-clamp-1">
          {product.name}
        </h3>
        
        <p className="text-xs text-zinc-400 line-clamp-1 mb-4 italic font-medium leading-relaxed">
          {product.description}
        </p>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6">
          <div className="flex items-center gap-1.5 text-zinc-500 text-[10px] font-bold uppercase tracking-widest">
            <SafeIcon icon={FiMapPin} className="text-[#D4B483] w-3 h-3" />
            <span>{product.origin_region}, {product.origin_country}</span>
          </div>
          <div className="flex items-center gap-1.5 text-zinc-400 text-[9px] font-black uppercase tracking-widest bg-zinc-50 px-2 py-1 rounded-lg">
            <SafeIcon icon={FiTruck} className="w-3 h-3" />
            <span>Gratis &gt; €150</span>
          </div>
        </div>
        
        <div className="flex items-center justify-between pt-6 border-t border-zinc-100">
          <div>
            <p className="text-[9px] font-black text-zinc-400 uppercase tracking-widest mb-1.5">{t('marketplace.priceLabel')}</p>
            <p className="text-3xl font-black text-zinc-900 tracking-tighter">
              €{product.price.toFixed(2)}
              <span className="text-sm font-medium text-zinc-300 ml-1.5 italic">/ {product.unit}</span>
            </p>
          </div>
          <motion.button 
            whileHover={{ scale: 1.1, backgroundColor: '#D4B483', color: '#1A2E22' }}
            whileTap={{ scale: 0.9 }}
            className="w-14 h-14 bg-zinc-900 text-white rounded-[1.25rem] flex items-center justify-center transition-all duration-300 shadow-xl shadow-zinc-200"
          >
            <SafeIcon icon={FiShoppingBag} className="w-6 h-6" />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};